import React, {useState, useEffect} from 'react';
import {
    TextField,
    Button,
    Container,
    Paper,
    Typography,
    Switch,
    FormControlLabel,
    CircularProgress,
    Snackbar,
    Alert,
    Stepper,
    Step,
    StepLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Divider,
    FormHelperText,
} from '@mui/material';
import axiosInstance from "../AxiosInstance";
import Box from "@mui/material/Box";

function ThirdPartyIntegrationsPage() {
    const [organisationIntegrationId, setOrganisationIntegrationId] = useState(null); // Optional ID
    const [apiId, setApiId] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [integrationEnabled, setIntegrationEnabled] = useState(false); // State for the toggle switch
    const [loading, setLoading] = useState(true); // Loading state
    const [savingSettings, setSavingSettings] = useState(false); // Loading state for settings save
    const [loadingProductGroups, setLoadingProductGroups] = useState(false); // Loading state for product groups
    const [error, setError] = useState(''); // Error state
    const [success, setSuccess] = useState(false); // Success notification state
    const [activeStep, setActiveStep] = useState(0); // Current step in the stepper
    const [productGroups, setProductGroups] = useState([]); // Available product groups from Unleashed
    const [selectedProductGroups, setSelectedProductGroups] = useState([]); // Selected product groups
    const [allProductsSelected, setAllProductsSelected] = useState(false); // Flag for "all products" option
    const [autoDisabledMessage, setAutoDisabledMessage] = useState(null);

    useEffect(() => {
        // Fetch organisation integration data when the page loads
        fetchIntegration();
    }, []);

    const fetchIntegration = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/settings');
            const data = response.data;

            // Populate fields with existing integration data
            setOrganisationIntegrationId(data.organisationIntegrationId || null);
            setApiId(data.apiId || ''); // Set API ID if available
            setApiKey(data.apiKey || ''); // Set API Key if available
            setIntegrationEnabled(data.isEnabled || false); // Set toggle state
            setSelectedProductGroups(data.productGroups || []);
            setAllProductsSelected(data.allProductsSelected || false);
            setAutoDisabledMessage(data.autoDisabledMessage);
        } catch (err) {
            if (err.response?.status === 404) {
                // No integration found, set fields to default
                setOrganisationIntegrationId(null);
                setApiId('');
                setApiKey('');
                setIntegrationEnabled(false);
                setSelectedProductGroups([]);
                setAllProductsSelected(false);
                setAutoDisabledMessage('');
            } else {
                // Other errors
                setError(
                    err.response?.data?.message ||
                    'Failed to fetch organisation integration. Please try again later.'
                );
            }
        } finally {
            setLoading(false); // Stop loading spinner
        }
    };

    const handleToggle = async (event) => {
        const isEnabled = event.target.checked;
        setSavingSettings(true);
        try {
            const payload = {
                organisationIntegrationId, // Include the ID if it exists
                apiId: apiId || null, // Send null if API ID is empty
                apiKey: apiKey || null, // Send null if API Key is empty
                isEnabled: isEnabled,
                productGroups: selectedProductGroups,
                allProductsSelected: allProductsSelected
            };

            await axiosInstance.put('/settings', payload);
            setIntegrationEnabled(isEnabled);
            
            if (!isEnabled && activeStep !== 0)
                setActiveStep(0);
            
            setSuccess(true);
            
            if (isEnabled) {
                setAutoDisabledMessage(null);
            }
            
            if (window.refreshNotifications)
                window.refreshNotifications();
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to save integration status. Please try again later.'
            );
        } finally {
            setSavingSettings(false);
        }
    };

    const fetchProductGroups = async (id, key) => {
        setLoadingProductGroups(true);
        try {
            const response = await axiosInstance.post('/settings/unleashedproductgroups', {
                apiId: id || apiId,
                apiKey: key || apiKey
            });
            setProductGroups(response.data || []);
            return true;
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to fetch product groups from Unleashed. Please check your API credentials.'
            );
            return false;
        } finally {
            setLoadingProductGroups(false);
        }
    };

    const handleNext = async () => {
        if (activeStep === 0) {
            // Validate required fields
            if (!apiId || !apiKey) {
                setError('Please enter both API ID and API Key.');
                return;
            }
            
            // Fetch product groups before proceeding to next step
            const success = await fetchProductGroups();
            if (success) {
                setActiveStep(1);
            }
        } else {
            await handleSave();
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleAllProductsChange = (event) => {
        const checked = event.target.checked;
        setAllProductsSelected(checked);
        if (checked) {
            // Clear selected product groups when "all products" is selected
            setSelectedProductGroups([]);
        }
    };

    const handleProductGroupsChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedProductGroups(typeof value === 'string' ? value.split(',') : value);
    };

    const handleSave = async () => {
        setSavingSettings(true);
        try {
            const payload = {
                organisationIntegrationId, // Include the ID if it exists
                apiId: apiId || null, // Send null if API ID is empty
                apiKey: apiKey || null, // Send null if API Key is empty
                isEnabled: integrationEnabled,
                productGroups: selectedProductGroups,
                allProductsSelected: allProductsSelected
            };

            await axiosInstance.put('/settings', payload); // Save updated integration details
            setSuccess(true); // Show success notification
            setActiveStep(0); // Reset to first step after successful save
            
            if (integrationEnabled) {
                setAutoDisabledMessage(null);
            }
            
            if (window.refreshNotifications)
                window.refreshNotifications();
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to save organisation integration. Please try again later.'
            );
        } finally {
            setSavingSettings(false);
        }
    };

    const steps = ['API Credentials', 'Product Group Selection'];

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <>
                        {/* API Credentials Form */}
                        <form style={{width: '100%'}}>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                label="API ID"
                                value={apiId}
                                onChange={(e) => setApiId(e.target.value)}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                label="API Key"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                            />
                        </form>
                    </>
                );
            case 1:
                return (
                    <>
                        <Typography variant="subtitle1" style={{marginTop: '16px', marginBottom: '8px'}}>
                            Select which Unleashed product groups to sync
                        </Typography>
                        
                        {/* All Products Option */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allProductsSelected}
                                    onChange={handleAllProductsChange}
                                    color="primary"
                                />
                            }
                            label="All Products"
                            style={{marginBottom: '16px', width: '100%'}}
                        />
                        
                        <Divider style={{width: '100%', marginBottom: '16px'}} />
                        
                        {/* Product Groups Multi-Select */}
                        <FormControl 
                            fullWidth 
                            variant="outlined" 
                            disabled={allProductsSelected || loadingProductGroups}
                            style={{marginBottom: '16px'}}
                        >
                            <InputLabel id="product-groups-label">Product Groups</InputLabel>
                            <Select
                                labelId="product-groups-label"
                                multiple
                                value={selectedProductGroups}
                                onChange={handleProductGroupsChange}
                                input={<OutlinedInput label="Product Groups" />}
                                renderValue={(selected) => selected.join(', ')}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}
                            >
                                {loadingProductGroups ? (
                                    <MenuItem disabled>
                                        <CircularProgress size={24} />
                                        <Typography style={{ marginLeft: '10px' }}>Loading product groups...</Typography>
                                    </MenuItem>
                                ) : (
                                    productGroups.map((group) => (
                                        <MenuItem key={group} value={group}>
                                            <Checkbox checked={selectedProductGroups.indexOf(group) > -1} />
                                            <ListItemText primary={group} />
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                            {!allProductsSelected && selectedProductGroups.length === 0 && (
                                <FormHelperText error>
                                    Please select at least one product group or enable "All Products"
                                </FormHelperText>
                            )}
                        </FormControl>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Box className="component-container">
            <Container component="main" maxWidth="sm">
                <Paper
                    elevation={3}
                    style={{
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography component="h1" variant="h5" gutterBottom>
                        Unleashed Integration
                    </Typography>

                    {autoDisabledMessage && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            <Typography>Integration was automatically disabled:</Typography>
                            <Typography sx={{ fontWeight: 'bold', my: 1 }}>{autoDisabledMessage}</Typography>
                            <Typography>Enable the integration again after resolving the issue.</Typography>
                        </Alert>
                    )}

                    {loading ? (
                        <CircularProgress style={{marginTop: '20px'}}/> // Show loading spinner
                    ) : (
                        <>
                            {/* Integration Enable/Disable Switch - Only visible when conditions are met */}
                            {apiId && apiKey && (allProductsSelected || selectedProductGroups.length > 0) && (
                                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 3 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={integrationEnabled}
                                                onChange={handleToggle}
                                                color="primary"
                                                disabled={savingSettings}
                                            />
                                        }
                                        label="Enabled"
                                    />
                                    {savingSettings && (
                                        <CircularProgress size={20} sx={{ ml: 1 }} />
                                    )}
                                </Box>
                            )}
                            
                            <Divider style={{width: '100%', marginBottom: '16px'}} />

                            {/* Configuration Wizard */}
                            <Typography variant="subtitle1" gutterBottom align="left" sx={{ width: '100%' }}>
                                Configuration
                            </Typography>

                            {/* Stepper */}
                            <Stepper activeStep={activeStep} style={{width: '100%', marginBottom: '20px'}}>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>

                            {/* Step Content */}
                            {renderStepContent(activeStep)}

                            {/* Navigation Buttons */}
                            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Button
                                    color="inherit"
                                    disabled={activeStep === 0}
                                    onClick={handleBack}
                                    sx={{ mr: 1 }}
                                >
                                    Back
                                </Button>
                                <Box sx={{ flex: '1 1 auto' }} />
                                {loadingProductGroups && (
                                    <CircularProgress size={24} sx={{ mr: 1 }} />
                                )}
                                <Button 
                                    onClick={handleNext}
                                    variant="contained"
                                    color="primary"
                                    disabled={
                                        (!apiId || !apiKey) && activeStep === 0 ||
                                        (activeStep === 1 && !allProductsSelected && selectedProductGroups.length === 0) ||
                                        loadingProductGroups ||
                                        savingSettings
                                    }
                                >
                                    {activeStep === steps.length - 1 ? 'Save' : 'Next'}
                                </Button>
                            </Box>
                        </>
                    )}
                </Paper>

                {/* Snackbar for Error Notification */}
                <Snackbar
                    open={!!error}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    autoHideDuration={6000}
                    onClose={() => setError('')}
                >
                    <Alert onClose={() => setError('')} severity="error" sx={{width: '100%'}}>
                        {error}
                    </Alert>
                </Snackbar>

                {/* Snackbar for Success Notification */}
                <Snackbar
                    open={success}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    autoHideDuration={6000}
                    onClose={() => setSuccess(false)}
                >
                    <Alert onClose={() => setSuccess(false)} severity="success" sx={{width: '100%'}}>
                        Changes saved successfully!
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
}

export default ThirdPartyIntegrationsPage;
